import * as Sentry from '@sentry/react'
import { FullStory, init as fsInit, isInitialized } from '@fullstory/browser'
import { UserIsAuth0User, UserIsPlatformUser } from 'utils/typeguards'
import { getRoles, getRolesFromUser } from 'utils/auth'

import { Auth0User } from 'models/auth'
import { PlatformUser } from 'models/user'
import { isPreviewHost } from './isPreviewHost'
import { logger } from 'utils/logging'

// @types/cookiebot-sdk isn't in the project `types` array, so reference the
// minimal shape we need locally instead of editing tsconfig.
declare global {
  interface Window {
    Cookiebot?: {
      consent?: { statistics?: boolean }
    }
  }
}

export const FULLSTORY_ORG_ID = process.env.REACT_APP_FULLSTORY_ORG_ID

// An org ID's suffix names its data realm. The SDK defaults to the US hosts, and
// their edge answers 404 for an EU1 org's settings, so an EU1 org (ours,
// o-AYS1-eu1) never starts recording unless it loads from and sends to EU1.
const FULLSTORY_EU1_HOSTS = { host: 'eu1.fullstory.com', script: 'edge.eu1.fullstory.com/s/fs.js' }
const fullstoryRealmHosts = (orgId: string) => (orgId.endsWith('-eu1') ? FULLSTORY_EU1_HOSTS : {})

export const fullstoryController: {
  instance: typeof FullStory | undefined
} = {
  instance: undefined,
}

// FullStory can't work for the rest of this page load: the org id is unset, the
// page is a preview host, or `fsInit` threw on invalid options. None recovers on
// retry, and `initFullstory` is re-entered from every `trackFullstoryUser` call
// and consent change, so latch it and short-circuit rather than re-running and
// re-logging each time (ISSUE-2943).
// `fsInit` only injects the fs.js script tag: a blocked script (ad blockers
// routinely block FullStory) or a wrong-realm settings 404 fails later and never
// throws. The `fullstory` Sentry tag is what tells those apart: it stays
// `loading` unless the SDK's `start` observer reports `recording` (ISSUE-4527).
let fullstoryUnavailable = false
let consentListenerRegistered = false
let pendingUser: Auth0User | PlatformUser | null = null

// Privacy: DOM redaction is layered:
//   1. Dashboard-side Privacy Rules (FullStory account) handle global defaults.
//   2. `fs-mask` (text -> dots, DOM kept) and `fs-exclude` (DOM omitted) CSS
//      classes mark sensitive elements at the component level. Always-sensitive
//      shared inputs (PhoneInput, TelephoneInput, MultiEmailInput) carry the
//      class internally; payment/billing/profile sections wrap with the class
//      explicitly. New forms that render PII or financial data must opt in.
// Consent: FullStory is gated on Cookiebot's `statistics` consent category.
// Until the user accepts statistics cookies, the SDK is not initialized and no
// PII is sent. Withdrawing consent shuts the SDK down for the current session.

/** Returns true only when Cookiebot is loaded and the user has granted statistics consent. */
const hasStatisticsConsent = (): boolean => {
  return typeof window !== 'undefined' && window.Cookiebot?.consent?.statistics === true
}

const registerConsentListeners = () => {
  if (consentListenerRegistered) return
  if (typeof window === 'undefined') return
  consentListenerRegistered = true

  const onConsentChange = () => {
    if (hasStatisticsConsent()) {
      // Consent granted: pick up where we left off if a user was waiting to be tracked.
      if (pendingUser && !fullstoryController.instance) trackFullstoryUser(pendingUser)
    } else {
      // Consent withdrawn: stop recording immediately.
      if (fullstoryController.instance) untrackFullstory()
    }
  }

  window.addEventListener('CookiebotOnAccept', onConsentChange)
  window.addEventListener('CookiebotOnDecline', onConsentChange)
  window.addEventListener('CookiebotOnConsentReady', onConsentChange)
}

export const initFullstory = () => {
  if (fullstoryController.instance) return true
  if (fullstoryUnavailable) return false

  // Preview images are built with the dev variables and would record into the
  // production FullStory org next to customer sessions. Sentry skips them too.
  if (isPreviewHost()) {
    fullstoryUnavailable = true
    return false
  }

  if (!FULLSTORY_ORG_ID) {
    fullstoryUnavailable = true
    // Console only: an unset build-time var is one deploy-config fact, not a
    // per-session runtime error. Reporting it burned ~82k Sentry events/month
    // (40% of this project's volume) and helped exhaust the org quota (ISSUE-2943).
    logger.warn('FullStory not initialized: REACT_APP_FULLSTORY_ORG_ID is not set', undefined, {
      sentry: false,
      context: { component: 'fullstory', action: 'initFullstory' }
    })
    return false
  }

  // Always register listeners so that a later consent grant boots the SDK.
  registerConsentListeners()

  if (!hasStatisticsConsent()) return false

  try {
    if (isInitialized()) {
      // `init` runs once per page load. If a logout or a consent withdrawal shut
      // the recorder down, capture has to be restarted explicitly.
      FullStory('restart')
    } else {
      fsInit({
        orgId: FULLSTORY_ORG_ID,
        devMode: import.meta.env.DEV,
        ...fullstoryRealmHosts(FULLSTORY_ORG_ID),
      })
      // Registered once, it fires on the first capture start and after every restart.
      FullStory('observe', {
        type: 'start',
        callback: ({ sessionUrl }) => {
          Sentry.setTag('fullstory', 'recording')
          Sentry.setContext('fullstory', { sessionUrl })
        },
      })
    }
    // Tags ride on events Sentry already sends, so this costs no quota.
    Sentry.setTag('fullstory', 'loading')
    fullstoryController.instance = FullStory
    return true
  } catch (e) {
    fullstoryUnavailable = true
    logger.error('FullStory init failed', e, {
      context: { component: 'fullstory', action: 'initFullstory' }
    })
    return false
  }
}

export const trackFullstoryUser = (user: Auth0User | PlatformUser) => {
  if (!user) return

  // Remember the user so a later consent grant can identify them.
  pendingUser = user

  if (!fullstoryController.instance) initFullstory()
  if (!fullstoryController.instance) {
    // Either no org id, or statistics consent has not been granted yet.
    // Both paths are intentional and already logged where appropriate.
    return
  }

  if (UserIsAuth0User(user)) {
    const roles = getRolesFromUser(user)
    fullstoryController.instance('setIdentity', {
      uid: user.sub,
      properties: {
        email: user.email,
        roles: Array.from(roles.all.keys()).join(', '),
      },
    })
  } else if (UserIsPlatformUser(user)) {
    // Use stable opaque id, not email, as the cross-system primary key.
    const uid = user.id || user.email
    getRoles()
      .then((roles) => {
        fullstoryController.instance?.('setIdentity', {
          uid,
          properties: {
            email: user.email,
            roles: Array.from(roles.all.keys()).join(', '),
          },
        })
      })
      .catch((e) => {
        logger.error('FullStory identify (PlatformUser) failed: getRoles rejected', e, {
          context: { component: 'fullstory', action: 'trackFullstoryUser' }
        })
        // Best-effort: identify without roles so the session is at least linked to the user.
        fullstoryController.instance?.('setIdentity', {
          uid,
          properties: {
            email: user.email,
          },
        })
      })
  }
}

export const untrackFullstory = () => {
  pendingUser = null
  if (!fullstoryController.instance) return
  // Anonymize the identity, then shut down recording so a subsequent user on a
  // shared workstation isn't captured under the previous session.
  fullstoryController.instance('setIdentity', { anonymous: true })
  fullstoryController.instance('shutdown')
  fullstoryController.instance = undefined
  Sentry.setTag('fullstory', 'shutdown')
  Sentry.setContext('fullstory', null)
}
